/* ============================================================
   Dungeon Hoard — procedural 8-bit audio (music + shared core)
   ------------------------------------------------------------
   No audio files ship with the game. A tiny Web-Audio synth builds
   NES-style voices — pulse waves, a triangle bass and a noise track —
   and a lookahead scheduler loops an upbeat, adventurous chiptune
   theme. The same AudioContext powers the sound effects (see sfx.ts),
   so one mute toggle governs everything. Self-hosted, no CDN, no blob:
   everything is generated.

   Public surface:
     setupMusic()  — wire the mute toggle, attempt autoplay on the
                     teaser, and arm a first-gesture fallback.
     audioOn(), withAudio()  — used by sfx.ts to share the context.
   ============================================================ */

const STORE_KEY = "dh-music"; // "on" | "off" (governs all sound)

/* ---------- shared audio core (music + sfx) ---------- */

let ctx: AudioContext | null = null;
let musicBus: GainNode | null = null;
let sfxBus: GainNode | null = null;
let pulse12: PeriodicWave | null = null; // 12.5% duty — lead
let pulse25: PeriodicWave | null = null; // 25% duty — pad / sfx
let pulse50: PeriodicWave | null = null; // 50% duty — bass-ish / sfx
let noiseBuf: AudioBuffer | null = null;
let enabled = true; // resolved in setupMusic()

/** Everything a voice needs to schedule a sound. */
export interface AudioBundle {
  ctx: AudioContext;
  bus: GainNode;
  pulse12: PeriodicWave;
  pulse25: PeriodicWave;
  pulse50: PeriodicWave;
  noise: AudioBuffer;
}

/** Band-limited pulse wave of the given duty cycle. */
function makePulse(c: AudioContext, duty: number, harmonics = 22): PeriodicWave {
  const real = new Float32Array(harmonics + 1);
  const imag = new Float32Array(harmonics + 1);
  for (let k = 1; k <= harmonics; k++) imag[k] = (2 / (k * Math.PI)) * Math.sin(k * Math.PI * duty);
  return c.createPeriodicWave(real, imag);
}

/** Lazily build the audio graph + waves + noise (first use only). */
function buildContext(): void {
  if (ctx) return;
  const AC: typeof AudioContext =
    (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  ctx = new AC();

  musicBus = ctx.createGain();
  musicBus.gain.value = 0.0001; // faded in when the loop starts
  musicBus.connect(ctx.destination);

  sfxBus = ctx.createGain();
  sfxBus.gain.value = 0.9;
  sfxBus.connect(ctx.destination);

  pulse12 = makePulse(ctx, 0.125);
  pulse25 = makePulse(ctx, 0.25);
  pulse50 = makePulse(ctx, 0.5);

  const n = Math.floor(ctx.sampleRate);
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < n; i++) data[i] = Math.random() * 2 - 1;
  noiseBuf = buf;
}

/** True if the player has sound enabled. */
export function audioOn(): boolean {
  return enabled;
}

/**
 * Run `fn` with a ready audio bundle, if sound is on. Builds the context on
 * demand and resumes it (SFX are always triggered by user gestures, so the
 * resume succeeds). A no-op when muted.
 */
export function withAudio(fn: (a: AudioBundle) => void): void {
  if (!enabled) return;
  buildContext();
  if (!ctx) return;
  if (ctx.state === "suspended") void ctx.resume();
  fn({ ctx, bus: sfxBus!, pulse12: pulse12!, pulse25: pulse25!, pulse50: pulse50!, noise: noiseBuf! });
}

/* ---------- the song ----------
   8 bars of 16 steps (loop = 128 steps). A minor / C major — upbeat and
   adventurous (Am · F · C · G, the heroic axis progression), played twice
   with a higher melodic variation the second pass. Driving drums, a bouncy
   octave bass and a sparkling 16th-note arpeggio. */

const STEPS_PER_BAR = 16;
const LOOP_STEPS = 8 * STEPS_PER_BAR; // 128
const BPM = 108; // upbeat

const SEMITONES: Record<string, number> = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4,
  F: 5, "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
};
function noteToFreq(name: string): number {
  const m = /^([A-G][#b]?)(\d)$/.exec(name);
  if (!m) return 440;
  const midi = (Number(m[2]) + 1) * 12 + SEMITONES[m[1]];
  return 440 * Math.pow(2, (midi - 69) / 12);
}
function octaveUp(name: string): string {
  const m = /^([A-G][#b]?)(\d)$/.exec(name)!;
  return m[1] + String(Number(m[2]) + 1);
}

interface Note {
  t: number;
  dur: number;
  note: string;
  vel?: number;
}

interface Chord {
  root: string; // bass root
  tones: string[]; // arpeggio tones (mid register)
}
const CHORDS: Chord[] = [
  { root: "A2", tones: ["A4", "C5", "E5", "A5"] }, // Am  (i)
  { root: "F2", tones: ["F4", "A4", "C5", "F5"] }, // F   (VI)
  { root: "C2", tones: ["C4", "E4", "G4", "C5"] }, // C   (III)
  { root: "G2", tones: ["G4", "B4", "D5", "G5"] }, // G   (VII)
];
const PROGRESSION = [0, 1, 2, 3, 0, 1, 2, 3]; // bar -> chord index

// Lead melody per bar (16 steps). [stepInBar, note, durSteps].
const LEAD_BARS: Array<Array<[number, string, number]>> = [
  [[0, "A4", 2], [2, "C5", 2], [4, "E5", 2], [6, "A5", 2], [8, "G5", 2], [10, "E5", 2], [12, "A5", 4]],
  [[0, "F4", 2], [2, "A4", 2], [4, "C5", 2], [6, "F5", 2], [8, "E5", 2], [10, "C5", 2], [12, "F5", 4]],
  [[0, "E4", 2], [2, "G4", 2], [4, "C5", 2], [6, "E5", 2], [8, "G5", 2], [10, "E5", 2], [12, "G5", 4]],
  [[0, "D5", 2], [2, "B4", 2], [4, "G4", 2], [6, "B4", 2], [8, "D5", 2], [10, "G5", 2], [12, "D5", 4]],
  [[0, "E5", 2], [2, "A5", 2], [4, "E5", 2], [6, "C5", 2], [8, "A4", 2], [10, "C5", 2], [12, "E5", 2], [14, "A5", 2]],
  [[0, "F5", 2], [2, "C5", 2], [4, "A4", 2], [6, "C5", 2], [8, "F5", 2], [10, "A5", 2], [12, "G5", 4]],
  [[0, "G5", 2], [2, "E5", 2], [4, "C5", 2], [6, "E5", 2], [8, "G5", 2], [10, "C6", 2], [12, "B5", 2], [14, "G5", 2]],
  [[0, "D5", 2], [2, "G5", 2], [4, "B5", 2], [6, "G5", 2], [8, "D5", 2], [10, "B4", 2], [12, "A4", 2], [14, "B4", 2]],
];

function buildSong(): { bass: Note[]; pad: Note[]; lead: Note[]; perc: Note[] } {
  const bass: Note[] = [];
  const pad: Note[] = [];
  const lead: Note[] = [];
  const perc: Note[] = [];

  PROGRESSION.forEach((ci, bar) => {
    const base = bar * STEPS_PER_BAR;
    const chord = CHORDS[ci];
    const rootHi = octaveUp(chord.root);

    // Bass: bouncy 8th notes alternating root / octave-up — drives the groove.
    for (let i = 0; i < 8; i++) {
      bass.push({ t: base + i * 2, dur: 2, note: i % 2 === 0 ? chord.root : rootHi, vel: i % 2 === 0 ? 1 : 0.8 });
    }

    // Pad: a sparkling 16th-note arpeggio cycling the chord tones (quiet).
    for (let i = 0; i < 16; i++) {
      pad.push({ t: base + i, dur: 1, note: chord.tones[i % chord.tones.length], vel: 0.42 });
    }

    // Lead: the melody, present in every bar so it's upbeat from the start.
    for (const [s, note, dur] of LEAD_BARS[bar]) lead.push({ t: base + s, dur, note, vel: 0.95 });

    // Drums: a driving backbeat — kick on 1 & 3, snare on 2 & 4, hats on 8ths.
    perc.push({ t: base + 0, dur: 1, note: "kick", vel: 1 });
    perc.push({ t: base + 8, dur: 1, note: "kick", vel: 0.95 });
    perc.push({ t: base + 4, dur: 1, note: "snare", vel: 0.85 });
    perc.push({ t: base + 12, dur: 1, note: "snare", vel: 0.85 });
    for (const s of [2, 6, 10, 14]) perc.push({ t: base + s, dur: 1, note: "hat", vel: 0.5 });
  });

  return { bass, pad, lead, perc };
}

/* ---------- the music engine ---------- */

class ChiptuneEngine {
  private song = buildSong();
  private byStep: Note[][][] = [];
  private voices: Array<keyof ReturnType<typeof buildSong>> = ["bass", "pad", "lead", "perc"];

  private timer: number | null = null;
  private nextStepTime = 0;
  private step = 0;
  private playing = false;

  private readonly stepDur = 60 / BPM / 4; // seconds per 16th note
  private readonly lookahead = 0.12;
  private readonly tick = 25;

  constructor() {
    this.byStep = this.voices.map(() => Array.from({ length: LOOP_STEPS }, () => [] as Note[]));
    this.voices.forEach((v, vi) => {
      for (const n of this.song[v]) this.byStep[vi][n.t % LOOP_STEPS].push(n);
    });
  }

  async start(): Promise<void> {
    buildContext();
    if (!ctx || !musicBus) return;
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {
        /* autoplay blocked — a later gesture will resume + start us */
      }
    }
    if (this.playing) return;
    this.playing = true;
    const now = ctx.currentTime;
    musicBus.gain.cancelScheduledValues(now);
    musicBus.gain.setValueAtTime(Math.max(0.0001, musicBus.gain.value), now);
    musicBus.gain.linearRampToValueAtTime(0.34, now + 0.9);
    this.nextStepTime = now + 0.06;
    this.step = 0;
    this.timer = window.setInterval(() => this.schedule(), this.tick);
  }

  stop(): void {
    if (!ctx || !musicBus || !this.playing) return;
    this.playing = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const now = ctx.currentTime;
    musicBus.gain.cancelScheduledValues(now);
    musicBus.gain.setValueAtTime(musicBus.gain.value, now);
    musicBus.gain.linearRampToValueAtTime(0.0001, now + 0.35);
  }

  isPlaying(): boolean {
    return this.playing;
  }
  suspend(): void {
    if (ctx && ctx.state === "running") void ctx.suspend();
  }
  resume(): void {
    if (this.playing && ctx && ctx.state === "suspended") void ctx.resume();
  }

  private schedule(): void {
    if (!ctx) return;
    while (this.nextStepTime < ctx.currentTime + this.lookahead) {
      const s = this.step % LOOP_STEPS;
      for (let vi = 0; vi < this.voices.length; vi++) {
        const voice = this.voices[vi];
        for (const n of this.byStep[vi][s]) this.playNote(voice, n, this.nextStepTime);
      }
      this.nextStepTime += this.stepDur;
      this.step++;
    }
  }

  private playNote(voice: string, n: Note, t: number): void {
    if (voice === "perc") return this.playPerc(n, t);
    const c = ctx!;
    const dur = n.dur * this.stepDur;
    const osc = c.createOscillator();
    const g = c.createGain();

    let peak: number, a: number, d: number, sus: number, rel: number;
    if (voice === "bass") {
      osc.setPeriodicWave(pulse50!);
      peak = 0.42; a = 0.006; d = 0.05; sus = 0.7; rel = 0.06;
    } else if (voice === "pad") {
      osc.setPeriodicWave(pulse25!);
      peak = 0.1; a = 0.004; d = 0.04; sus = 0.4; rel = 0.04;
    } else {
      osc.setPeriodicWave(pulse12!);
      peak = 0.18; a = 0.006; d = 0.05; sus = 0.65; rel = 0.1;
    }
    peak *= n.vel ?? 1;

    osc.frequency.setValueAtTime(noteToFreq(n.note), t);
    const gp = g.gain;
    gp.setValueAtTime(0, t);
    gp.linearRampToValueAtTime(peak, t + a);
    gp.linearRampToValueAtTime(peak * sus, t + a + d);
    gp.setValueAtTime(peak * sus, t + Math.max(a + d, dur));
    gp.linearRampToValueAtTime(0.0001, t + dur + rel);

    osc.connect(g);
    g.connect(musicBus!);
    osc.start(t);
    osc.stop(t + dur + rel + 0.02);
  }

  private playPerc(n: Note, t: number): void {
    const c = ctx!;
    if (n.note === "kick") {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(135, t);
      osc.frequency.exponentialRampToValueAtTime(45, t + 0.12);
      const v = 0.36 * (n.vel ?? 1);
      g.gain.setValueAtTime(v, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
      osc.connect(g);
      g.connect(musicBus!);
      osc.start(t);
      osc.stop(t + 0.2);
    } else if (n.note === "snare") {
      const src = c.createBufferSource();
      src.buffer = noiseBuf;
      const bp = c.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 1900;
      bp.Q.value = 0.8;
      const g = c.createGain();
      const v = 0.14 * (n.vel ?? 1);
      g.gain.setValueAtTime(v, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      src.connect(bp);
      bp.connect(g);
      g.connect(musicBus!);
      src.start(t);
      src.stop(t + 0.13);
    } else {
      const src = c.createBufferSource();
      src.buffer = noiseBuf;
      const hp = c.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 7500;
      const g = c.createGain();
      const v = 0.05 * (n.vel ?? 1);
      g.gain.setValueAtTime(v, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.045);
      src.connect(hp);
      hp.connect(g);
      g.connect(musicBus!);
      src.start(t);
      src.stop(t + 0.05);
    }
  }
}

/* ---------- controls / wiring ---------- */

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
function wantsSound(): boolean {
  try {
    const stored = localStorage.getItem(STORE_KEY);
    if (stored === "on") return true;
    if (stored === "off") return false;
  } catch {
    /* ignore */
  }
  return !prefersReducedMotion();
}
function remember(on: boolean): void {
  try {
    localStorage.setItem(STORE_KEY, on ? "on" : "off");
  } catch {
    /* ignore */
  }
}

/**
 * Wire the floating mute toggle, attempt to start the theme as soon as the
 * teaser screen appears, and arm a one-time first-gesture fallback (browsers
 * block audio until a user gesture, so the theme reliably begins on the very
 * first interaction if autoplay was refused).
 */
export function setupMusic(): void {
  const engine = new ChiptuneEngine();
  const btn = document.getElementById("musicToggle");
  enabled = wantsSound();

  const paint = (): void => {
    if (!btn) return;
    btn.setAttribute("aria-pressed", String(enabled));
    btn.classList.toggle("is-off", !enabled);
    btn.setAttribute("aria-label", enabled ? "Mute sound" : "Play sound");
    btn.setAttribute("title", enabled ? "Mute sound" : "Play sound");
  };
  paint();

  // Try to play immediately (on the teaser). Most browsers will hold this
  // suspended until a gesture; the listener below resumes + (re)starts then.
  if (enabled) void engine.start();

  let armed = true;
  const firstGesture = (): void => {
    if (!armed) return;
    armed = false;
    window.removeEventListener("pointerdown", firstGesture);
    window.removeEventListener("keydown", firstGesture);
    if (enabled) void engine.start();
  };
  window.addEventListener("pointerdown", firstGesture);
  window.addEventListener("keydown", firstGesture);

  btn?.addEventListener("click", () => {
    enabled = !enabled;
    remember(enabled);
    paint();
    if (enabled) void engine.start();
    else engine.stop();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) engine.suspend();
    else engine.resume();
  });
}
